import ChatService from '@/services/ChatService'
import {
  connectSocket,
  joinRoom,
  leaveRoom,
  onEvent,
} from '@/services/SocketService'
import { defineStore } from 'pinia'

const CHAT_MESSAGE_EVENT = 'chat:message'
const CHAT_READ_EVENT = 'chat:read'
const CHAT_CONVERSATION_EVENT = 'chat:conversation'

function asArray(value) {
  if (Array.isArray(value))
    return value

  return []
}

function unwrapList(response, keys = []) {
  if (Array.isArray(response))
    return response

  const data = response?.data || response || {}

  for (const key of keys) {
    if (Array.isArray(data?.[key]))
      return data[key]
    if (Array.isArray(data?.items?.[key]))
      return data.items[key]
  }

  if (Array.isArray(data?.items))
    return data.items
  if (Array.isArray(data?.results))
    return data.results
  if (Array.isArray(data?.data))
    return data.data

  return []
}

function unwrapItem(response, keys = []) {
  const data = response?.data || response || {}

  for (const key of keys) {
    if (data?.[key])
      return data[key]
    if (data?.item?.[key])
      return data.item[key]
  }

  return data?.item || data?.data || data
}

function getStoredUser() {
  const firstName = localStorage.getItem('first_name') || ''
  const lastName = localStorage.getItem('last_name') || ''
  const username = localStorage.getItem('username') || ''
  const email = localStorage.getItem('email') || ''

  return {
    id: localStorage.getItem('userId') || localStorage.getItem('user_id') || '',
    fullName: [firstName, lastName].filter(Boolean).join(' ') || username || email || 'Current User',
    email,
    role: localStorage.getItem('role') || 'User',
    status: 'online',
    participantType: 'user',
  }
}

function compactName(...parts) {
  return parts.filter(Boolean).join(' ').trim()
}

function getEntityId(raw = {}) {
  return raw.id || raw._id || raw.uuid || raw.user_id || raw.userId || raw.client_id || raw.clientId || raw.contact_id || raw.contactId || raw.participant_id || raw.participantId || ''
}

function normalizeParticipant(raw = {}, fallbackType = 'user') {
  const id = getEntityId(raw)
  const participantType = raw.participant_type || raw.participantType || raw.type || raw.entity_type || raw.entityType || fallbackType
  const fullName = raw.fullName
    || raw.full_name
    || raw.name
    || raw.displayName
    || raw.display_name
    || compactName(raw.first_name || raw.firstName, raw.last_name || raw.lastName)
    || raw.username
    || raw.email
    || 'Unknown Contact'

  return {
    ...raw,
    id,
    key: `${participantType}:${id || fullName}`,
    participantId: id,
    participantType,
    fullName,
    email: raw.email || '',
    phone: raw.phone || raw.phone_number || raw.phoneNumber || '',
    avatar: raw.avatar || raw.avatar_url || raw.avatarUrl || raw.photo || raw.image || '',
    role: raw.role || raw.title || raw.position || (participantType === 'client' ? 'Customer' : 'Team member'),
    status: raw.status || raw.presence || 'offline',
    about: raw.about || raw.description || raw.notes || raw.email || '',
  }
}

function getCurrentUserId() {
  return localStorage.getItem('userId') || localStorage.getItem('user_id') || ''
}

function normalizeMessage(raw = {}, currentUserId = getCurrentUserId(), conversationId = '') {
  const senderId = raw.sender_id || raw.senderId || raw.user_id || raw.userId || raw.from_id || raw.fromId || raw.author_id || raw.authorId || ''
  const id = raw.id || raw._id || raw.uuid || `${conversationId || 'message'}-${senderId}-${raw.created_at || raw.createdAt || raw.time || Date.now()}`
  const createdAt = raw.created_at || raw.createdAt || raw.sent_at || raw.sentAt || raw.time || raw.timestamp || new Date().toISOString()
  const text = raw.message || raw.text || raw.content || raw.body || ''
  const isOutgoing = String(senderId) === String(currentUserId) || raw.direction === 'outgoing' || raw.isMine || raw.mine

  return {
    ...raw,
    id,
    conversationId: raw.conversation_id || raw.conversationId || raw.chat_id || raw.chatId || conversationId,
    senderId,
    senderType: raw.sender_type || raw.senderType || 'user',
    text,
    message: text,
    direction: isOutgoing ? 'outgoing' : 'incoming',
    createdAt,
    status: raw.status || (raw.feedback?.isSeen ? 'seen' : raw.feedback?.isDelivered ? 'delivered' : 'sent'),
  }
}

function selectConversationParticipant(raw = {}, currentUserId = getCurrentUserId()) {
  if (raw.participant)
    return raw.participant
  if (raw.contact)
    return raw.contact
  if (raw.user)
    return raw.user
  if (raw.client)
    return { ...raw.client, participant_type: 'client' }

  const participants = asArray(raw.participants || raw.members || raw.users)
  const participant = participants.find(item => String(getEntityId(item)) !== String(currentUserId)) || participants[0]

  return participant || {}
}

function sortConversationsByRecent(conversations) {
  return [...conversations].sort((a, b) => new Date(b.lastMessageAt || 0).getTime() - new Date(a.lastMessageAt || 0).getTime())
}

function normalizeConversation(raw = {}, currentUserId = getCurrentUserId()) {
  const id = raw.id || raw._id || raw.uuid || raw.conversation_id || raw.conversationId || raw.chat_id || raw.chatId || ''
  const messages = asArray(raw.messages || raw.chat?.messages).map(message => normalizeMessage(message, currentUserId, id))
  const lastMessage = raw.last_message || raw.lastMessage || raw.chat?.lastMessage || messages[messages.length - 1] || {}
  const participant = normalizeParticipant(selectConversationParticipant(raw, currentUserId), raw.participant_type || raw.participantType || 'user')
  const normalizedLastMessage = lastMessage?.id || lastMessage?.message || lastMessage?.text
    ? normalizeMessage(lastMessage, currentUserId, id)
    : null

  return {
    ...raw,
    id,
    room: raw.room || raw.room_id || raw.roomId || `chat:${id}`,
    participant,
    messages,
    lastMessagePreview: raw.lastMessagePreview || raw.last_message_preview || normalizedLastMessage?.text || '',
    lastMessageAt: raw.lastMessageAt || raw.last_message_at || normalizedLastMessage?.createdAt || messages[messages.length - 1]?.createdAt || raw.updated_at || raw.updatedAt || raw.created_at || raw.createdAt || '',
    unreadCount: Number(raw.unreadCount ?? raw.unread_count ?? raw.unseenMsgs ?? raw.chat?.unseenMsgs ?? 0),
    ai_status: raw.ai_status || raw.aiStatus || 'TINO_ACTIVE',
    ai_briefing: raw.ai_briefing || raw.aiBriefing || null,
    provider: raw.provider || raw.channel || null,
  }
}

function normalizeContact(raw = {}) {
  const fallbackType = raw.client_id || raw.clientId || raw.customer_id || raw.customerId ? 'client' : 'user'

  return {
    ...normalizeParticipant(raw, raw.participant_type || raw.participantType || fallbackType),
    isContact: true,
  }
}

function mergeConversation(existing, incoming) {
  if (!existing)
    return incoming

  const messages = incoming.messages?.length ? incoming.messages : existing.messages

  return {
    ...existing,
    ...incoming,
    messages,
  }
}

export const useChatStore = defineStore('ChatStore', {
  state: () => ({
    currentUser: getStoredUser(),
    conversations: [],
    contacts: [],
    activeConversation: null,
    loading: false,
    sending: false,
    search: '',
    _activeRoom: null,
    _unsubscribeRealtime: [],
  }),

  getters: {
    activeMessages() {
      return this.activeConversation?.messages || []
    },
    hasActiveConversation() {
      return Boolean(this.activeConversation?.id)
    },
    profileUser() {
      return this.currentUser
    },
  },

  actions: {
    setSearch(search = '') {
      this.search = search
    },

    async fetchProfile() {
      try {
        const response = await ChatService.getProfile()
        const profile = unwrapItem(response, ['profile', 'user', 'participant'])

        this.currentUser = normalizeParticipant({ ...getStoredUser(), ...profile, status: profile.status || 'online' }, 'user')

        return this.currentUser
      } catch (error) {
        console.warn('Unable to fetch chat profile, using local session data:', error)
        this.currentUser = normalizeParticipant(getStoredUser(), 'user')

        return this.currentUser
      }
    },

    async fetchSidebar(search = this.search) {
      this.loading = true
      this.search = search || ''

      try {
        const params = this.search ? { search: this.search } : {}
        const [conversationsResponse, contactsResponse] = await Promise.all([
          ChatService.getConversations(params),
          ChatService.getContacts(params),
        ])

        this.conversations = sortConversationsByRecent(
          unwrapList(conversationsResponse, ['conversations', 'chats']).map(conversation => normalizeConversation(conversation, this.currentUser.id)),
        )
        this.contacts = unwrapList(contactsResponse, ['contacts', 'users', 'clients']).map(normalizeContact)

        return {
          conversations: this.conversations,
          contacts: this.contacts,
        }
      } catch (error) {
        console.error('Error fetching chat sidebar:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async openConversation(target) {
      this.loading = true

      try {
        const isConversation = Boolean(target?.messages || target?.participant || target?.lastMessageAt || target?.lastMessagePreview)
        const response = isConversation
          ? await ChatService.getConversation(target.id)
          : await ChatService.createDirectConversation({
            participant_id: target.participantId || target.id,
            participant_type: target.participantType || 'user',
            participantId: target.participantId || target.id,
            participantType: target.participantType || 'user',
          })

        const rawConversation = unwrapItem(response, ['conversation', 'chat'])
        const conversation = normalizeConversation(rawConversation, this.currentUser.id)
        const existingIndex = this.conversations.findIndex(item => String(item.id) === String(conversation.id))

        if (existingIndex === -1)
          this.conversations.unshift(conversation)
        else
          this.conversations.splice(existingIndex, 1, mergeConversation(this.conversations[existingIndex], conversation))

        this.conversations = sortConversationsByRecent(this.conversations)
        this.activeConversation = this.conversations.find(item => String(item.id) === String(conversation.id)) || conversation

        this.joinActiveConversationRoom()
        await this.markConversationRead(conversation.id, { silent: true })

        return this.activeConversation
      } catch (error) {
        console.error('Error opening chat conversation:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async sendMessage(text) {
      if (!this.activeConversation?.id)
        throw new Error('Select a conversation before sending a message')

      this.sending = true

      try {
        const response = await ChatService.sendMessage(this.activeConversation.id, { message: text, text })
        const rawMessage = unwrapItem(response, ['message'])
        const message = normalizeMessage(rawMessage?.id ? rawMessage : { message: text, ...rawMessage }, this.currentUser.id, this.activeConversation.id)

        this.applyIncomingMessage({ ...message, conversation_id: this.activeConversation.id })

        return message
      } catch (error) {
        console.error('Error sending chat message:', error)
        throw error
      } finally {
        this.sending = false
      }
    },

    async markConversationRead(conversationId = this.activeConversation?.id, options = {}) {
      if (!conversationId)
        return null

      try {
        const response = await ChatService.markConversationRead(conversationId)

        this.applyReadReceipt({ conversation_id: conversationId, user_id: this.currentUser.id })

        return response
      } catch (error) {
        if (!options.silent) {
          console.error('Error marking chat as read:', error)
          throw error
        }

        return null
      }
    },

    upsertConversation(conversation) {
      const normalized = normalizeConversation(conversation, this.currentUser.id)
      const index = this.conversations.findIndex(item => String(item.id) === String(normalized.id))

      if (index === -1)
        this.conversations.unshift(normalized)
      else
        this.conversations.splice(index, 1, mergeConversation(this.conversations[index], normalized))

      this.conversations = sortConversationsByRecent(this.conversations)

      if (this.activeConversation && String(this.activeConversation.id) === String(normalized.id))
        this.activeConversation = mergeConversation(this.activeConversation, normalized)

      return normalized
    },

    applyIncomingMessage(payload = {}) {
      const conversationId = payload.conversation_id || payload.conversationId || payload.chat_id || payload.chatId || payload.conversation?.id || this.activeConversation?.id
      const message = normalizeMessage(payload.message || payload, this.currentUser.id, conversationId)
      let conversation = this.conversations.find(item => String(item.id) === String(conversationId))

      if (!conversation && payload.conversation) {
        conversation = this.upsertConversation(payload.conversation)
      }

      if (!conversation)
        return

      const exists = conversation.messages.some(item => String(item.id) === String(message.id))
      if (!exists)
        conversation.messages.push(message)

      conversation.lastMessagePreview = message.text
      conversation.lastMessageAt = message.createdAt

      if (this.activeConversation && String(this.activeConversation.id) === String(conversation.id)) {
        this.activeConversation = { ...conversation, unreadCount: 0 }
        conversation.unreadCount = 0
      } else if (message.direction === 'incoming') {
        conversation.unreadCount += 1
      }

      this.conversations = sortConversationsByRecent(this.conversations)
    },

    applyReadReceipt(payload = {}) {
      const conversationId = payload.conversation_id || payload.conversationId || payload.chat_id || payload.chatId || this.activeConversation?.id
      const conversation = this.conversations.find(item => String(item.id) === String(conversationId))

      if (!conversation)
        return

      conversation.unreadCount = 0
      conversation.messages = conversation.messages.map(message => ({
        ...message,
        status: message.direction === 'outgoing' ? 'seen' : message.status,
      }))

      if (this.activeConversation && String(this.activeConversation.id) === String(conversationId))
        this.activeConversation = { ...conversation }
    },

    applyConversationUpdate(payload = {}) {
      const conversation = payload.conversation || payload

      if (!conversation)
        return

      this.upsertConversation(conversation)
    },

    async updateAiStatus(conversationId, aiStatus) {
      const conv = this.conversations.find(c => String(c.id) === String(conversationId))
      if (conv) conv.ai_status = aiStatus
      if (this.activeConversation && String(this.activeConversation.id) === String(conversationId))
        this.activeConversation = { ...this.activeConversation, ai_status: aiStatus }
      try {
        await ChatService.updateAiStatus(conversationId, aiStatus)
      } catch (e) {
        // rollback on error
        if (conv) conv.ai_status = aiStatus === 'TINO_ACTIVE' ? 'PAUSED_FOR_HUMAN' : 'TINO_ACTIVE'
      }
    },

    initializeRealtime() {
      if (this._unsubscribeRealtime.length)
        return

      connectSocket()

      // Join org-level room for new conversation events
      const orgId = localStorage.getItem('orgId') || localStorage.getItem('org_id')
      if (orgId) joinRoom(`org:${orgId}`)

      this._unsubscribeRealtime = [
        onEvent(CHAT_MESSAGE_EVENT, payload => this.applyIncomingMessage(payload)),
        onEvent(CHAT_READ_EVENT, payload => this.applyReadReceipt(payload)),
        onEvent(CHAT_CONVERSATION_EVENT, payload => this.applyConversationUpdate(payload)),
        onEvent('chat:new_conversation', payload => {
          const normalized = normalizeConversation(payload?.conversation || payload, this.currentUser.id)
          if (normalized.id) this.upsertConversation(normalized)
        }),
      ]

      this.joinActiveConversationRoom()
    },

    joinActiveConversationRoom() {
      const room = this.activeConversation?.room || (this.activeConversation?.id ? `chat:${this.activeConversation.id}` : null)

      if (!room || this._activeRoom === room)
        return

      if (this._activeRoom)
        leaveRoom(this._activeRoom)

      this._activeRoom = room
      joinRoom(room)
    },

    cleanupRealtime() {
      this._unsubscribeRealtime.forEach(unsubscribe => unsubscribe?.())
      this._unsubscribeRealtime = []

      if (this._activeRoom)
        leaveRoom(this._activeRoom)

      this._activeRoom = null
    },
  },
})
